import { collection, getDocs, addDoc, serverTimestamp, query, where, orderBy, limit, startAfter, increment, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc, deleteDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { scrapeWebsite } from './scraper';
import { auth } from '../firebase/config'; // Add this import

// Helper function to normalize keywords
const normalizeKeyword = (keyword) => {
  return keyword.toLowerCase().replace(/[^a-z0-9]/g, '');
};

// Sample dummy data to fallback to in case of errors
const dummyListings = [
  {
    id: '1',
    name: 'Coffee Shop',
    description: 'A cozy coffee shop with great pastries',
    category: 'Food & Dining',
    address: '123 Main St, Anytown',
    phone: '(555) 123-4567',
    website: 'https://example.com',
    email: 'info@coffeeshop.com',
    imageUrl: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24',
    rating: 4.5,
    coordinates: { lat: 40.712776, lng: -74.005974 }
  },
  {
    id: '2',
    name: 'Tech Store',
    description: 'Latest gadgets and tech accessories',
    category: 'E-commerce & Retail',
    address: '456 Tech Blvd, Techville',
    phone: '(555) 987-6543',
    website: 'https://techstore-example.com',
    email: 'sales@techstore.com',
    imageUrl: 'https://images.unsplash.com/photo-1498049794561-7780e7231661',
    rating: 4.2,
    coordinates: { lat: 40.722776, lng: -74.015974 }
  },
  {
    id: '3',
    name: 'Green Garden Restaurant',
    description: 'Farm-to-table dining experience',
    category: 'Food & Drink',
    address: '789 Garden Rd, Greenville',
    phone: '(555) 456-7890',
    website: 'https://greengarden.example.com',
    email: 'hello@greengarden.com',
    imageUrl: 'https://images.unsplash.com/photo-1556742111-a301076d9d18',
    rating: 4.7,
    coordinates: { lat: 40.732776, lng: -74.025974 }
  }
];

// Add this function before createListing
const incrementSitesCount = async () => {
  try {
    console.log('incrementing sites count');
    const statsRef = doc(db, 'stats', 'site-metrics');
    const statsDoc = await getDoc(statsRef);
    
    if (!statsDoc.exists()) {
      // Initialize stats document if it doesn't exist
      await setDoc(statsRef, { 
        totalSites: 1,
        totalSitesPublished: 0 
      });
    } else {
      // Increment the existing count
      await updateDoc(statsRef, {
        totalSites: increment(1)
      });
    }
  } catch (error) {
    console.error('Error updating stats:', error);
    // Don't throw error to prevent blocking listing creation
  }
};

const decrementSitesCount = async () => {
  try {
    const statsRef = doc(db, 'stats', 'site-metrics');
    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      await updateDoc(statsRef, {
        totalSites: increment(-1)
      });
    }
  } catch (error) {
    console.error('Error decrementing sites count:', error);
  }
};

const updatePublishedSitesCount = async (shouldIncrement) => {
  try {
    const statsRef = doc(db, 'stats', 'site-metrics');
    const statsDoc = await getDoc(statsRef);
    
    if (!statsDoc.exists()) {
      // Initialize stats document if it doesn't exist
      await setDoc(statsRef, { 
        totalSites: 0,
        totalSitesPublished: shouldIncrement ? 1 : 0 
      });
    } else {
      // Update the published count using Firestore increment function
      await updateDoc(statsRef, {
        totalSitesPublished: increment(shouldIncrement ? 1 : -1)
      });
    }
  } catch (error) {
    console.error('Error updating published sites count:', error);
  }
};

export const createListing = async (listingData, userId, userEmail) => {
  try {
    // Check if URL already exists
    console.log('createListing: Checking if URL exists...');
    const urlExists = await checkUrlExists(listingData.url);
    if (urlExists) {
      throw new Error('This website URL already exists in another listing');
    }

    const isUrlValid = await validateUrl(listingData.url);
    console.log('createListing: URL is valid:', isUrlValid);
    // Add derived keyword fields if keywords exist
    const updatedData = { ...listingData };
    if (listingData.keywords && listingData.keywords.length > 0) {
      listingData.keywords.forEach((keyword, index) => {
        updatedData[`derivedKeyword${index + 1}`] = normalizeKeyword(keyword);
      });
    }

    const docRef = await addDoc(collection(db, 'listings'), {
      ...updatedData,
      userId,
      userEmail,
      isPublished: false,
      isUrlValid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    console.log('createListing: Listing created:', docRef.id);
    // Increment the sites count after successful listing creation
    await incrementSitesCount();
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating listing:', error);
    throw new Error(error.message || 'Failed to create listing');
  }
};

// Update the publish function
export const publishListing = async (id) => {
  try {
    const docRef = doc(db, 'listings', id);
    const lastPublishedAt = serverTimestamp();
    await updateDoc(docRef, {
      isPublished: true,
      lastPublishedAt,
      publishedAt: lastPublishedAt,
      updatedAt: lastPublishedAt,
    });
    
    // Increment published sites count
    await updatePublishedSitesCount(true);
    
    return { id, lastPublishedAt: new Date() };
  } catch (error) {
    console.error('Error publishing listing:', error);
    throw new Error('Failed to publish listing');
  }
};

// Update the unpublish function
export const unpublishListing = async (id) => {
  try {
    const docRef = doc(db, 'listings', id);
    await updateDoc(docRef, {
      isPublished: false,
      updatedAt: serverTimestamp(),
    });
    
    // Decrement published sites count
    await updatePublishedSitesCount(false);
  } catch (error) {
    console.error('Error unpublishing listing:', error);
    throw new Error('Failed to unpublish listing');
  }
};

export const deleteListing = async (id) => {
  try {
    const listingDocRef = doc(db, 'listings', id);
    // Get the listing data before deletion to check if it was published
    const docSnap = await getDoc(listingDocRef);
    
    if (docSnap.exists()) {
      const listingData = docSnap.data();
      await deleteDoc(listingDocRef);
      
      // Decrement total sites count
      await decrementSitesCount();
      
      // If the listing was published, also decrement published count
      if (listingData.isPublished) {
        await updatePublishedSitesCount(false);
      }
    }
  } catch (error) {
    console.error("Error deleting listing:", error);
    throw error;
  }
};

export const getUserListings = async (userId) => {
  try {
    const q = query(collection(db, 'listings'), where('userId', '==', userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching user listings:', error);
    return [];
  }
};

export const getListing = async (id) => {
  try {
    const docRef = doc(db, 'listings', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    throw new Error('Listing not found');
  } catch (error) {
    console.error('Error fetching listing:', error);
    throw error;
  }
};

export const checkUrlExists = async (url, excludeId = null) => {
  try {
    const q = query(
      collection(db, 'listings'),
      where('url', '==', url)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return false;
    }

    // If we're updating an existing listing, exclude it from the check
    if (excludeId) {
      const docs = querySnapshot.docs.filter(doc => doc.id !== excludeId);
      return docs.length > 0;
    }

    return true;
  } catch (error) {
    console.error('Error checking URL existence:', error);
    throw new Error('Failed to check URL availability');
  }
};

export const updateListing = async (id, listingData, shouldValidateUrl = false) => {
  try {
    const isUrlValid = shouldValidateUrl ? await validateUrl(listingData.url) : listingData.isUrlValid;
    const lastSavedAt = serverTimestamp();
    
    // Ensure keywords is an array or remove it if undefined
    let keywords = listingData.keywords;
    if (keywords === undefined) {
      keywords = [];
    }
    
    // Create update data with derived keyword fields
    const updateData = {
      ...listingData,
      keywords,
      isUrlValid,
      lastSavedAt,
      updatedAt: lastSavedAt,
    };

    // Add derived keyword fields
    if (keywords.length > 0) {
      // First clear any existing derived keyword fields
      for (let i = 1; i <= 6; i++) { // Assuming max 20 keywords
        updateData[`derivedKeyword${i}`] = null;
      }
      // Then add new normalized derived keyword fields
      keywords.forEach((keyword, index) => {
        updateData[`derivedKeyword${index + 1}`] = normalizeKeyword(keyword);
      });
    }

    // Check if URL already exists (excluding current listing if updating)
    const urlExists = await checkUrlExists(listingData.url, id);
    if (urlExists) {
      throw new Error('This website URL already exists in another listing');
    }

    if (!id) {
      // Creating new listing
      const newDocRef = await addDoc(collection(db, 'listings'), {
        ...updateData,
        createdAt: lastSavedAt,
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        isPublished: false
      });
      
      // Increment sites count for new listings
      await incrementSitesCount();
      
      return { 
        id: newDocRef.id, 
        isUrlValid, 
        lastSavedAt: new Date() 
      };
    }

    // Updating existing listing
    const docRef = doc(db, 'listings', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Listing not found');
    }

    await updateDoc(docRef, updateData);
    return { 
      id, 
      isUrlValid, 
      lastSavedAt: new Date() 
    };
  } catch (error) {
    console.error('Error updating listing:', error);
    throw new Error(error.message || 'Failed to update listing');
  }
};

// New helper function to validate URL
const validateUrl = async (url) => {
  try {
    const scrapedData = await scrapeWebsite(url);
    return !!scrapedData;
  } catch (error) {
    return false;
  }
};

export const getPaginatedListings = async (category = null, pageSize = 15, lastVisible = null, searchTerm = null) => {
  try {
    const listingsRef = collection(db, 'listings');
    const baseConstraints = [where('isPublished', '==', true)];

    // Add category constraint if specified
    if (category) {
      baseConstraints.push(where('category', '==', category));
    }

    if (searchTerm) {
      const normalizedSearchTerm = searchTerm.toLowerCase().replace(/[^a-z0-9]/g, '');
      
      // Create queries for each derived keyword field
      const queries = Array.from({ length: 6 }, (_, i) => {
        const keywordField = `derivedKeyword${i + 1}`;
        return query(
          listingsRef,
          ...baseConstraints,
          where(keywordField, '>=', normalizedSearchTerm),
          where(keywordField, '<', normalizedSearchTerm + '\uf8ff'),
          orderBy(keywordField),
          orderBy('name', 'asc')
        );
      });

      // Execute all queries in parallel
      const snapshots = await Promise.all(queries.map(q => getDocs(q)));

      // Combine results and remove duplicates using Set
      const seenIds = new Set();
      const allListings = [];
      
      snapshots.forEach(snapshot => {
        snapshot.docs.forEach(doc => {
          if (!seenIds.has(doc.id)) {
            seenIds.add(doc.id);
            allListings.push({ id: doc.id, ...doc.data() });
          }
        });
      });

      // Total count is the number of unique documents across all queries
      const totalCount = allListings.length;

      // Sort combined results by name
      allListings.sort((a, b) => a.name.localeCompare(b.name));

      // Handle pagination manually since we're combining results
      const startIndex = lastVisible ? allListings.findIndex(item => item.id === lastVisible.id) + 1 : 0;
      const paginatedListings = allListings.slice(startIndex, startIndex + pageSize);
      
      const returnValue = {
        listings: paginatedListings,
        lastVisible: paginatedListings[paginatedListings.length - 1],
        hasMore: startIndex + pageSize < allListings.length,
        totalCount
      };
      
      console.log('Search results metadata:', {
        totalCount: returnValue.totalCount,
        hasMore: returnValue.hasMore,
        lastVisibleId: returnValue.lastVisible?.id,
        category,
        searchTerm
      });
      
      return returnValue;
    }

    // If no search term, use simple pagination with name ordering
    let queryConstraints = [
      ...baseConstraints,
      orderBy('name', 'asc'),
    ];

    // Get total count using getCountFromServer (for non-search case)
    const countQuery = query(listingsRef, ...baseConstraints);
    const countSnapshot = await getCountFromServer(countQuery);
    const totalCount = countSnapshot.data().count;

    // Add startAfter constraint if we have a lastVisible cursor
    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }

    // Add limit constraint
    queryConstraints.push(limit(pageSize));

    // Build and execute query
    const listingsQuery = query(listingsRef, ...queryConstraints);
    const snapshot = await getDocs(listingsQuery);

    if (snapshot.empty) {
      const returnValue = {
        listings: [],
        lastVisible: null,
        hasMore: false,
        totalCount: 0
      };
      
      console.log('Empty results metadata:', {
        totalCount: returnValue.totalCount,
        hasMore: returnValue.hasMore,
        category,
        searchTerm
      });
      
      return returnValue;
    }

    const listings = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    const returnValue = {
      listings,
      lastVisible: snapshot.docs[snapshot.docs.length - 1],
      hasMore: listings.length === pageSize,
      totalCount
    };
    
    console.log('Regular query metadata:', {
      totalCount: returnValue.totalCount,
      hasMore: returnValue.hasMore,
      lastVisibleId: returnValue.lastVisible?.id,
      category,
      searchTerm,
      resultsCount: listings.length
    });
    
    return returnValue;
  } catch (error) {
    console.error('Error fetching paginated listings:', error);
    const returnValue = {
      listings: dummyListings.slice(0, pageSize),
      lastVisible: null,
      hasMore: false,
      totalCount: dummyListings.length
    };
    
    console.log('Error fallback metadata:', {
      totalCount: returnValue.totalCount,
      hasMore: returnValue.hasMore,
      category,
      searchTerm,
      error: error.message
    });
    
    return returnValue;
  }
};

export const getAllCategories = async () => {
  try {
    const listingsRef = collection(db, 'listings');
    const snapshot = await getDocs(listingsRef);

    if (snapshot.empty) {
      return ["All Categories"];
    }

    const categories = new Set(["All Categories"]);
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (data.category) {
        categories.add(data.category);
      }
    });

    return Array.from(categories);
  } catch (error) {
    console.error('Error fetching all categories:', error);
    return ["All Categories"];
  }
};

export const subscribeToStats = (callback) => {
  const statsRef = doc(db, 'stats', 'site-metrics');
  
  return onSnapshot(statsRef, (doc) => {
    if (doc.exists()) {
      callback(doc.data());
    } else {
      callback({ totalSites: 0, totalSitesPublished: 0 });
    }
  });
};